import React from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { useLoungesByExperienceType } from '@/hooks/loungesHooks/loungesHelperHooks';
import { Lounge } from '@/types';
import { Card, LoungeTypes } from '@/constants';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { getLocaleData } from '@/selectors/localeSelectors';
import { getLastViewedProduct } from '@/selectors/lastViewedProductSelectors';
import * as loungeHelper from '@/helpers/loungeHelper';
import {
  DEFAULT_STICKY_GLOBAL_HEADER_OFFSET,
  getBasePathFromLocation,
  getStickyGlobalHeaderOffset,
  smoothScrollToElement,
} from '@/helpers/utils';
import SearchLoungeResult from '../common/SearchLoungeResult';
import SearchLoungeResultNoCarousel from '../common/SearchLoungeResultNoCarousel';
import Loading from '../common/Loading';
import CenturionNetworkNav from './CenturionNetworkNav';

const CANONICAL_ORIGIN = 'https://www.americanexpress.com';

interface LoungeItemListJsonLdOptions {
  lounges: Lounge[];
  name: string;
  locale: string;
  product: string;
}

export const createLoungeItemListJsonLd = ({
  lounges,
  name,
  locale,
  product,
}: LoungeItemListJsonLdOptions) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name,
  itemListOrder: 'https://schema.org/ItemListOrderAscending',
  numberOfItems: lounges.length,
  itemListElement: lounges.map((lounge, index) => {
    const loungeTerminal = lounge.location?.terminal;
    const airportIata = lounge.airport?.iata;
    const loungeUrl =
      loungeTerminal && airportIata
        ? `${CANONICAL_ORIGIN}${getBasePathFromLocation(locale)}/${product}/${airportIata}/${loungeHelper.generateLoungeUrl(
            {
              locale,
              loungeId: lounge.id,
              loungeName: lounge.name,
              loungeTerminal,
            }
          )}/`
        : undefined;
    const airport =
      lounge.airport?.name || airportIata
        ? {
            '@type': 'Airport',
            ...(airportIata && { iataCode: airportIata }),
            ...(lounge.airport?.name && { name: lounge.airport.name }),
          }
        : undefined;

    return {
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'LocalBusiness',
        ...(loungeUrl && { '@id': loungeUrl, url: loungeUrl }),
        name: lounge.name,
        ...(airport && { containedInPlace: airport }),
      },
    };
  }),
});

export const CenturionNetworkLounges = () => {
  const hasScrolledFromHashRef = React.useRef(false);
  const { data, error, isLoading } =
    useLoungesByExperienceType('CENTURION,SIDECAR');
  const { locale } = useSelector(getLocaleData);
  const product = useSelector(getLastViewedProduct) ?? Card.Platinum;
  const {
    centurionNetwork: {
      header,
      description,
      navHeader,
      centurionHeader,
      centurionDescription,
      sidecarHeader,
      sidecarDescription,
    },
  } = useCommonContent();

  React.useEffect(() => {
    if (
      isLoading ||
      !data ||
      hasScrolledFromHashRef.current ||
      typeof window === 'undefined'
    ) {
      return;
    }

    if (window.location.hash !== '#centurion-network-lounges-header') {
      return;
    }

    const target = document.querySelector('#centurion-network-lounges-header');
    if (target) {
      hasScrolledFromHashRef.current = true;
      smoothScrollToElement({
        element: target,
        offset: getStickyGlobalHeaderOffset(),
      });
    }
  }, [isLoading, data]);

  if (error) {
    return null;
  }

  if (isLoading) {
    return (
      <Loading
        size="lg"
        containerClassName="flex flex-justify-center dls-white-bg pad-4 margin-t"
      />
    );
  }

  const centurionLounges = data
    ?.filter(
      (lounge: Lounge) => lounge.experience_type === LoungeTypes.CENTURION
    )
    .sort((a: Lounge, b: Lounge) =>
      (a.airport?.location?.city ?? '').localeCompare(
        b.airport?.location?.city ?? ''
      )
    );
  const sidecarLounges = data
    ?.filter((lounge: Lounge) => lounge.experience_type === LoungeTypes.SIDECAR)
    .sort((a: Lounge, b: Lounge) =>
      (a.airport?.location?.city ?? '').localeCompare(
        b.airport?.location?.city ?? ''
      )
    );

  const lounges = [...(centurionLounges ?? []), ...(sidecarLounges ?? [])];
  const itemListJsonLd = createLoungeItemListJsonLd({
    lounges,
    name: header,
    locale,
    product,
  });

  return (
    <>
      {lounges.length > 0 && (
        <Helmet>
          {/* https://schema.org/ItemList */}
          <script
            id="centurion-network-lounges-item-list"
            type="application/ld+json"
          >
            {JSON.stringify(itemListJsonLd, null, 2)}
          </script>
        </Helmet>
      )}
      <hr className="tw-col-span-6 tw-col-start-2 tw-my-10 md_tw-hidden" />
      <h2
        id="centurion-network-lounges-header"
        className="tw-heading-md-regular tw-pb-1 tw-text-gray-900"
      >
        {header}
      </h2>
      <p className="tw-font-sans tw-text-base tw-text-gray-900">
        {description}
      </p>
      <hr className="tw-col-span-6 tw-col-start-2 tw-my-10 tw-hidden md_tw-block" />
      <div className="md_tw-grid md_tw-grid-cols-12 md_tw-gap-8">
        <div
          className="md_tw-sticky md_tw-col-span-3 md_tw-block md_tw-self-start"
          style={{ top: `${DEFAULT_STICKY_GLOBAL_HEADER_OFFSET}px` }}
        >
          <CenturionNetworkNav
            navHeader={navHeader}
            centurionHeader={centurionHeader}
            centurionDescription={centurionDescription}
            sidecarHeader={sidecarHeader}
            sidecarDescription={sidecarDescription}
          />
        </div>
        {/* Lounges content */}
        <div className="md_tw-col-span-9">
          <div id="centurion-lounges-section">
            <h3 className="tw-heading-xs-bold tw-text-gray-900 sm_tw-pt-10 md_tw-hidden">
              {centurionHeader}
            </h3>
            <p className="tw-mb-4 tw-block tw-font-sans tw-text-base tw-text-gray-900 md_tw-hidden">
              {centurionDescription}
            </p>
            <div className="tw-relative tw-grid tw-grid-cols-2 tw-place-items-center tw-gap-4 lg_tw-grid-cols-3 lg_tw-gap-6 x2l_tw-gap-8">
              {centurionLounges?.map((lounge: Lounge) => (
                <React.Fragment key={lounge.id}>
                  <div className="tw-hidden tw-w-full sm_tw-block">
                    <SearchLoungeResult
                      heading={lounge.airport?.location?.city}
                      headingLevel={3}
                      description={`${lounge.airport?.iata} • ${lounge.airport?.name}`}
                      lounge={lounge}
                      hideLoungeStatus={true}
                      hideLoungeTypeBadge={true}
                      trackElement="A Worldwide Collection of Exceptional Spaces"
                      trackDetail={`Centurion Lounge>>${lounge.airport?.iata}`}
                    />
                  </div>
                  <div className="tw-block tw-w-full sm_tw-hidden">
                    <SearchLoungeResultNoCarousel
                      heading={lounge.airport?.location?.city}
                      headingLevel={3}
                      description={`${lounge.airport?.iata}`}
                      lounge={lounge}
                      hideLoungeStatus={true}
                      trackElement="A Worldwide Collection of Exceptional Spaces"
                      trackDetail={`Centurion Lounge>>${lounge.airport?.iata}`}
                    />
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
          <hr className="tw-my-10 tw-hidden tw-border-gray-300 md_tw-block" />
          <div id="sidecar-lounges-section">
            <h3 className="tw-heading-xs-bold tw-pt-6 tw-text-gray-900 sm_tw-pt-10 md_tw-hidden">
              {sidecarHeader}
            </h3>
            <p className="tw-block tw-font-sans tw-text-base tw-text-gray-900 md_tw-hidden">
              {sidecarDescription}
            </p>
            <div className="tw-relative tw-grid tw-grid-cols-2 tw-place-items-center tw-gap-4 tw-pt-4 sm_tw-gap-8 md_tw-pt-0 lg_tw-grid-cols-3">
              {sidecarLounges?.map((lounge: Lounge) => (
                <React.Fragment key={lounge.id}>
                  <div className="tw-hidden tw-w-full sm_tw-block">
                    <SearchLoungeResult
                      heading={lounge.airport?.location?.city}
                      headingLevel={3}
                      description={`${lounge.airport?.iata} • ${lounge.airport?.name}`}
                      lounge={lounge}
                      hideLoungeStatus={true}
                      hideLoungeTypeBadge={true}
                      trackElement="A Worldwide Collection of Exceptional Spaces"
                      trackDetail={`Sidecar By The Centurion Lounge>>${lounge.airport?.iata}`}
                    />
                  </div>
                  <div className="tw-block tw-w-full sm_tw-hidden">
                    <SearchLoungeResultNoCarousel
                      heading={lounge.airport?.location?.city}
                      headingLevel={3}
                      description={`${lounge.airport?.iata}`}
                      lounge={lounge}
                      hideLoungeStatus={true}
                      trackElement="A Worldwide Collection of Exceptional Spaces"
                      trackDetail={`Sidecar by The Centurion Lounge>>${lounge.airport?.iata}`}
                    />
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
